import { Pause, Play } from 'lucide-react';
import { useState, type CSSProperties, type PointerEvent } from 'react';
import type { CanvasPlayer } from '../../../lib/home3-player';
import { useTranslation } from '../../../lib/i18n';
import { IconButton, Typography } from '../ui';
import styles from './Home3Player.module.css';

/** The scrubber's resolution: the range input's steps across the day. */
const STEPS = 10_000;

/**
 * The canvas's player band (Figma 2045:101937): the foot of the canvas, 80 high on the page's own surface
 * under a hairline — play, the scrubber, the time, then the map's zoom pill, which `HomeMap` portals into
 * `zoomSlot` so it stays wired to the camera.
 *
 * It drives the canvas's replay of what the reader has not seen yet (`useCanvasPlayer`), as the "Symphony Live"
 * prototype's player does: play / pause (pause stops the panel and the octopus where they are), the scrubber
 * seeks, and the readout counts the items shown so far over the items there are ("3/12"). Hovering the
 * scrubber shows the time under the pointer and thickens the track. The scrubber is a real range input, so
 * it takes the keyboard too. With nothing new there is nothing to play: "0/0", and play is off.
 */
export function Home3Player({ zoomSlotRef, player }: { zoomSlotRef: (el: HTMLDivElement | null) => void; player: CanvasPlayer | null }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState<number | null>(null);
  const progress = player?.progress ?? 0;
  const playing = player?.playing ?? false;

  const onHover = (e: PointerEvent<HTMLInputElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    setHover(Math.max(0, Math.min(1, (e.clientX - r.left) / Math.max(1, r.width))));
  };
  const hoverAt = hover === null || !player ? null : player.timeAt(hover);
  const hoverLabel = hoverAt === null ? '' : new Date(hoverAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const empty = !player || player.total === 0;

  return (
    <div className={styles.player}>
      <IconButton
        size="tiny"
        className={styles.play}
        aria-label={t(playing ? 'home.map.pause' : 'home.map.play')}
        title={t(playing ? 'home.map.pause' : 'home.map.play')}
        disabled={empty}
        onClick={player?.toggle}
      >
        {playing ? <Pause size={16} fill="currentColor" aria-hidden /> : <Play size={16} fill="currentColor" aria-hidden />}
      </IconButton>
      <div className={styles.track}>
        <input
          type="range"
          className={`${styles.scrubber}${hover !== null ? ` ${styles.scrubberHover}` : ''}`}
          min={0}
          max={STEPS}
          value={Math.round(progress * STEPS)}
          disabled={empty}
          aria-label={t('home.map.timeline')}
          aria-valuetext={hoverLabel || undefined}
          style={{ '--played': `${progress * 100}%` } as CSSProperties}
          onChange={(e) => player?.seek(Number(e.target.value) / STEPS)}
          onPointerMove={onHover}
          onPointerLeave={() => setHover(null)}
        />
        {hoverLabel && (
          <Typography
            variant="tiny-semi-bold"
            color="primary"
            component="span"
            className={styles.hoverTime}
            style={{ insetInlineStart: `${(hover ?? 0) * 100}%` }}
          >
            {hoverLabel}
          </Typography>
        )}
      </div>
      <Typography variant="tiny-semi-bold" color="primary" component="span" className={styles.time}>
        {player ? `${player.reached}/${player.total}` : ''}
      </Typography>
      <div ref={zoomSlotRef} className={styles.zoom} />
    </div>
  );
}
